/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

// components imports
import { SettingsFormElements } from "@/components/ProjectsComponents/Settings/SettingsFormElements";
import { Modal, Form, Input, Button, message, Row, Col, Typography } from "antd";
import { ErrorsComponent } from "@/components/UI/ErrorsComponent";
// misc imports
import { AUTH_FORM_LABEL_COL, AUTH_FORM_MAX_WIDTH, AUTH_FORM_WRAPPER_COL } from "@/constants";
import { IRequestError } from "@/interfaces/ServerError.interface";
import { useCreateProject } from "@/api/projects";
import { useServiceTypes } from "@/api/services";
import { IServiceTypeSettings } from "@/interfaces/Service.interface";
import { groupByFirstPartOfInputName } from "@/utils/form/groupByFirstPartOfInputName";
import { IProjectServicesProp } from "@/interfaces/Project.interface";

type FieldType = {
  title: string;
  service_type: string;
};

type ILocalSettings = Record<string, { settings: IServiceTypeSettings[] }>;

/**
 * The Create Project Modal
 * @returns {JSX.Element}
 */
export const CreateProjectModal = ({ isModalOpen, onClose }: { isModalOpen: boolean; onClose: () => void }): JSX.Element => {
  const { t } = useTranslation();
  const { workspaceId } = useParams();
  const [form] = Form.useForm();
  const [fetchedSettings, setFetchedSettings] = useState<ILocalSettings>();

  const { mutate: createProjectMutation, isLoading: isCreatingProject, error: createProjectError } = useCreateProject();

  const { data: serviceTypesList } = useServiceTypes();

  const [messageApi, contextHolder] = message.useMessage();

  // USE EFFECTS

  // Effect to set fetched settings and custom actions based on project type
  useEffect(() => {
    if (serviceTypesList && serviceTypesList.length) {
      const _settings: ILocalSettings = {};

      serviceTypesList.forEach((service) => {
        if (!service.settings.length || !service.is_active) return;
        _settings[service.key] = { settings: [] };
        _settings[service.key].settings = service.settings;
      });

      setFetchedSettings(_settings);
    }
  }, [serviceTypesList]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: Record<string, string>) => {
    const { title } = values;

    const groupedData = groupByFirstPartOfInputName(values);

    const services: IProjectServicesProp[] = [];
    Object.entries(groupedData)?.forEach(([key, values]) => {
      if (key === "title") return;
      services.push({
        service_type: key,
        enabled: true,
        settings: { ...values },
      });
    });

    createProjectMutation(
      {
        workspaceId: workspaceId as string,
        title,
        services: [...services],
      },
      {
        onSuccess: () => {
          messageApi.open({
            content: t("create_project.success_message"),
            type: "success",
            duration: 8,
          });
          handleClose();
        },
      }
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={t("create_project.title")}
        open={isModalOpen}
        onCancel={handleClose}
        style={{ maxWidth: 600 }}
        width={"95%"}
        centered
        footer={null}
        destroyOnClose
      >
        <Row>
          <Col xs={24}>
            <Form
              name="project-form"
              labelCol={{ span: AUTH_FORM_LABEL_COL }}
              labelAlign="left"
              wrapperCol={{ span: AUTH_FORM_WRAPPER_COL }}
              style={{ maxWidth: AUTH_FORM_MAX_WIDTH }}
              onFinish={onFinish}
              autoComplete="off"
              form={form}
            >
              {/* Project title */}
              <Form.Item<FieldType>
                label={t("create_project.project_title")}
                name="title"
                rules={[
                  {
                    required: true,
                    message: t("validation.required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
              {/* ./ Project title */}

              {/* Render Settings Form Elements */}
              {fetchedSettings &&
                Object.entries(fetchedSettings)?.map(([settingKey, value]) => {
                  const serviceTitle = serviceTypesList?.find((service) => service.key === settingKey)?.title || settingKey;
                  return (
                    <div key={settingKey}>
                      <Typography.Paragraph>
                        <strong>{`${serviceTitle} ${t('projects.settings')}`}</strong>
                      </Typography.Paragraph>
                      <SettingsFormElements settingsKey={settingKey} settings={value.settings} />
                    </div>
                  );
                })}

              {/* Submit button */}
              <Form.Item
                className="mb-0 float-right"
              >
                <Button loading={isCreatingProject} type="primary" htmlType="submit">
                  {t("create_project.submit")}
                </Button>
                <div className="mt-2">
                  <ErrorsComponent error={createProjectError as unknown as IRequestError} />
                </div>
              </Form.Item>
              {/* Submit button */}
            </Form>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
